/**
 * Ad unit tags — renders the templates in `src/data/adTagTemplates.ts` into
 * the adn.js request tags and VAST URLs a publisher places on their pages or
 * in their video player, and writes them to downloadable files.
 */
import writeXlsxFile, { type Row } from 'write-excel-file/browser'
import { saveBlob } from '@/utils/download'
import { adServerHost } from '@/services/creatives/creativeTags'
import type { AdUnit } from '@/services/adUnits/adUnitsApi'
import {
  adTagTemplates,
  defaultAdUnit,
  defaultTargetDiv,
  vastTemplate,
  vastVersions,
  type AdTagTemplate,
} from '@/data/adTagTemplates'

const API_BASE = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'

export type AdUnitTagDownload = 'INDIVIDUAL_REQUESTS' | 'SINGLE_REQUEST' | 'VAST'

/** Whether the tags tell the ad server GDPR applies; `UNSET` leaves it to the consent framework. */
export type GdprApplies = 'UNSET' | 'APPLIES' | 'NOT_APPLIES'

export interface AdUnitTagOptions {
  networkTagId: string
  gdpr: GdprApplies
  /**
   * Holds the ad request back until the ad unit is within `proximity` pixels
   * of the viewport. `0` requests as the unit comes into view.
   */
  lazyRequest?: { proximity: number }
  /**
   * Repeats the same ad request after the ad has been seen: `delay` seconds
   * after the `event`, `count` times over.
   */
  refresh?: { delay: number; count: number; event: RefreshEvent }
}

/** When the refresh clock starts: once the ad is viewable, or merely visible. */
export type RefreshEvent = 'onViewable' | 'onVisible'

export type TagAdUnit = Pick<AdUnit, 'tagId' | 'name'> & { site?: AdUnit['site'] }

/** The adn.js `env` matching the API environment, or null for production. */
export function adnEnv(): string | null {
  if (API_BASE.includes('localhost')) return 'localhost'
  if (API_BASE.includes('api.dev.')) return 'dev'
  if (API_BASE.includes('api.staging.')) return 'staging'
  return null
}

function gdprValue(gdpr: GdprApplies): 0 | 1 | null {
  return gdpr === 'APPLIES' ? 1 : gdpr === 'NOT_APPLIES' ? 0 : null
}

const PLACEHOLDER = /\{\{(\w+)\}\}/g
const LONE_PLACEHOLDER = /^(\s*)\{\{(\w+)\}\}\s*$/

/**
 * Fills a template's placeholders. A line holding only a placeholder is
 * dropped when it renders empty, and indented line by line when it renders
 * several lines. Unknown placeholders are left in place so they stand out.
 */
export function renderTemplate(template: string, values: Record<string, string>): string {
  const out: string[] = []
  for (const line of template.split('\n')) {
    const lone = line.match(LONE_PLACEHOLDER)
    if (lone && lone[2] in values) {
      const value = values[lone[2]]
      if (value) out.push(...value.split('\n').map((l) => lone[1] + l))
      continue
    }
    out.push(line.replace(PLACEHOLDER, (match, key: string) => (key in values ? values[key] : match)))
  }
  return out.join('\n')
}

function adUnitValues(adUnit: TagAdUnit): Record<string, string> {
  return { auTagId: adUnit.tagId, auName: adUnit.name, siteName: adUnit.site?.name ?? '' }
}

export function findAdTagTemplate(id: string): AdTagTemplate {
  return adTagTemplates.find((t) => t.id === id) ?? adTagTemplates[0]
}

/** One adn.js request for all of `adUnits`, using `template`. */
export function renderAdTag(template: AdTagTemplate, adUnits: TagAdUnit[], opts: AdUnitTagOptions): string {
  const env = adnEnv()
  const gdpr = gdprValue(opts.gdpr)
  return renderTemplate(template.template, {
    networkTagId: opts.networkTagId,
    env: env ? `env: '${env}',` : '',
    gdpr: gdpr !== null ? `gdpr: ${gdpr},` : '',
    requestParams: opts.lazyRequest
      ? `requestParams: { load: 'lazyRequest', proximity: ${opts.lazyRequest.proximity} },`
      : '',
    refresh: opts.refresh
      ? `refresh: { delay: ${opts.refresh.delay}, count: ${opts.refresh.count}, event: '${opts.refresh.event}' },`
      : '',
    targetDivs: adUnits.map((au) => renderTemplate(template.targetDiv ?? defaultTargetDiv, adUnitValues(au))).join('\n'),
    adUnits: adUnits.map((au) => renderTemplate(template.adUnit ?? defaultAdUnit, adUnitValues(au))).join(',\n'),
  })
}

/** The VAST URL a video player requests for `adUnit`. */
export function renderVastTag(adUnit: TagAdUnit, vastVersion: string, opts: AdUnitTagOptions): string {
  const gdpr = gdprValue(opts.gdpr)
  return renderTemplate(vastTemplate, {
    ...adUnitValues(adUnit),
    deliveryHost: adServerHost(),
    networkTagId: opts.networkTagId,
    vastVersion,
    gdprParam: gdpr !== null ? `gdpr=${gdpr}&` : '',
  })
}

const header = (value: string) => ({
  value,
  fontWeight: 'bold' as const,
  color: '#ffffff',
  backgroundColor: '#0d5d9e',
  height: 25,
  alignVertical: 'center' as const,
})

/** Builds and saves the tag file for `adUnits`, named after `fileBase`. */
export async function downloadAdUnitTags(
  download: AdUnitTagDownload,
  adUnits: TagAdUnit[],
  opts: AdUnitTagOptions & { templateId: string; vastVersion: string; fileBase: string }
): Promise<void> {
  const { fileBase } = opts

  if (download === 'VAST') {
    const version = vastVersions.find((v) => v.id === opts.vastVersion) ?? vastVersions[0]
    const rows: Row[] = [
      ['Ad unit', 'Ad unit ID', 'Site', 'VAST version', 'VAST tag'].map(header),
      ...adUnits.map((au) => [
        { value: au.name },
        { value: au.tagId },
        { value: au.site?.name ?? '' },
        { value: version.label },
        { value: renderVastTag(au, version.id, opts) },
      ]),
    ]
    const blob = await writeXlsxFile(rows, {
      columns: [{ width: 40 }, { width: 20 }, { width: 30 }, { width: 14 }, { width: 100 }],
    }).toBlob()
    saveBlob(blob, `${fileBase}-${version.id}.xlsx`)
    return
  }

  const template = findAdTagTemplate(opts.templateId)
  const text =
    download === 'INDIVIDUAL_REQUESTS'
      ? adUnits.map((au) => renderAdTag(template, [au], opts)).join('\n\n\n')
      : renderAdTag(template, adUnits, opts)
  const suffix = download === 'INDIVIDUAL_REQUESTS' ? 'tags' : 'request'
  saveBlob(new Blob([text + '\n'], { type: 'text/plain' }), `${fileBase}-${suffix}.txt`)
}
